
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import DateSelector from '@/components/generic/DateSelector.vue'
import FullSearchInput from '@/components/fillgrid/full-search-input.vue'
import moment from 'moment'
import { DateUtils } from '@/services/DateFormat'

import { FillgridColumn, FillgridFilters, GeoDistanceFillgridFilter, TemplateQuestionsTypes } from '@/types/Fillgrid'
import { CustomSearch, SearchDateType, SearchOrigin } from '@/types/catalogs/CustomSearch'
import FillgridService from '@/services/FillgridService'
import { PROCESS_STATUS, UNDEFINED_ENTITY_ID, UserCatalogFilters } from '@/types/Catalogs'
import { OPERATOR, STRING } from '@/types/GeneralConstants'
import { FILLGRID_REGEXPS } from '@/types/RegExpressions'
import FillgridGeoDistanceFilter from '@/components/fillgrid/FillgridGeoDistanceFilter.vue'
import localStorageUser from '@/store/localStoreUser'
import userInfoStore from '@/store/userInfo'
import { PageRequest } from '@/types/Support'
import config from '@/config'
import cookiesService, { BM_COOKIES } from '@/services/support/CookiesService'

// eslint-disable-next-line
enum StatiFill {
  // eslint-disable-next-line
  ALL,
  // eslint-disable-next-line
  WORKED,
  // eslint-disable-next-line
  NOT_WORKED
}

const INDENT_0: number = 0
const INDENT_1: number = 1
const INDENT_2: number = 2

@Component({
  components: {
    DateSelector,
    FullSearchInput,
    FillgridGeoDistanceFilter
  }
})
export default class FillgridFiltersComponent extends Vue {
  @Prop({ required: true })
  templateId: number

  @Prop({ required: false })
  viewId: number

  @Prop({ required: false, default: false })
  loading: boolean

  @Prop({ required: false, default: () => [] })
  columns: FillgridColumn[]

  @Prop({ required: false, default: () => [] })
  identsNames: string[]

  @Prop({ required: false, default: () => [] })
  identsLabels: string[]

  @Prop({ required: false, default: () => [] })
  users: string[]

  @Prop({ required: false, default: () => [] })
  searches: CustomSearch[]

  @Prop({ required: true })
  searchIdSelected: number

  @Prop({ required: true })
  searchUpdated: number // 0: init, 1: success, 2: error

  @Prop({ required: true })
  isUpdating: boolean // 0: init, 1: success, 2: error

  StatiFill = StatiFill
  columnsFiltered: any[] = []

  filters = new FillgridFilters()
  filtersBackup = new FillgridFilters()

  searchDateTypes = SearchDateType

  savedFiltersList: any[] = []
  savedFilterSelected = 0
  namefiltersToSave = ''
  savedFilterNameSelected = ''

  customFilterSelected = ''

  dateRangeShortcuts: any[] = []

  itemsFullSearch: string[] = []

  fullSearchVisible = false
  loadingUsers = false

  filterTypes = [
    TemplateQuestionsTypes.TEXT,
    TemplateQuestionsTypes.INTEGER,
    TemplateQuestionsTypes.DECIMAL,
    TemplateQuestionsTypes.PHONE,
    TemplateQuestionsTypes.EMAIL,
    TemplateQuestionsTypes.CURRENCY,
    TemplateQuestionsTypes.DATE,
    TemplateQuestionsTypes.DATETIME,
    TemplateQuestionsTypes.TIME,
    TemplateQuestionsTypes.SELECT
  ]
  PROCESS_STATUS = PROCESS_STATUS

  lastRequestTimestamp: Date
  lastRequestFilters = new FillgridFilters()
  secondsToWaitForNextRequest = 90
  millisecondsToWaitForNextRequest = this.secondsToWaitForNextRequest * 1000

  created () {
    this.createDateSelectorRangeShortcuts()
    this.columnsFiltered = this.columns.filter(it => it.name !== '_priority' && !it.base && (this.filterTypes.includes(it.type)))
    this.savedFiltersList = JSON.parse(localStorage.getItem('bm-fillgrid-filters-template-' + this.templateId) || '[]')
    this.init()
  }

  mounted () {
    const lastFillgridSearch = FillgridService.loadCustomSearchFromLocalStorage()
    if (lastFillgridSearch) {
      if (lastFillgridSearch.templateId === this.templateId) {
        this.updateFiltersValues(lastFillgridSearch.filters)
      } else {
        FillgridService.clearCustomSearchInLocalStorage()
      }
    }
    this.lastRequestTimestamp = new Date()
    this.lastRequestTimestamp.setTime(0)
    this.search()
  }

  @Watch('searchIdSelected')
  watchSearchIdSelected (currentSearch: number, oldSearch: number) {
    if (currentSearch > 0) {
      FillgridService.getSearch(this.templateId, currentSearch)
        .then((response) => {
          this.updateFiltersValues(response as CustomSearch)
          if (response.view && response.view > 0) {
            this.emitViewSelected(response.view)
          } else {
            this.clearViewSelected()
          }
        })
    } else {
      this.setBackupWith(this.filters)
      this.addIdentsToFilters(new CustomSearch())
      if (this.filters.view <= 0) this.clearViewSelected()
    }
  }

  @Watch('searchUpdated')
  watchSearchUpdated (searchId: number, oldSearchId: number) {
    if (searchId) {
      setTimeout(() => {
        this.$emit('search-updated', PROCESS_STATUS.INIT)
      }, 3000)
    }
  }

  @Watch('savedFilterSelected')
  watchSavedFiltersSelected (value: any) {
    this.loadSavedFilters(value)
  }

  @Watch('filters.user')
  filtersUserWatcher () {
    if (this.filters.fullHistory) {
      this.validateFTSFilters()
    }
  }

  @Watch('filters.fullSearch')
  filtersFullSearchWatcher () {
    if (this.filters.fullHistory) {
      this.validateFTSFilters()
    }
  }

  @Watch('filters.custom')
  filtersCustomWatcher () {
    if (this.filters.fullHistory) {
      this.validateFTSFilters()
    }
  }

  @Watch('filters.geoDistance.kilometers')
  filterGeoDistanceWatcher () {
    if (this.filters.fullHistory) {
      this.validateFTSFilters()
    }
  }

  get isSelectableSearch (): boolean {
    return this.selectableSearches.some((search) => search.id === this.searchIdSelected)
  }

  get selectableSearches () : CustomSearch[] {
    return this.searches.filter((search) => search.origin === SearchOrigin.FILLGRID)
  }

  /** Emit the call to update the value of the search selected */
  emitSearchSelected (searchId: number) {
    this.$emit('update-search', searchId)
  }

  /** Emit the event to update the value of the view selected */
  emitViewSelected (viewId: number) {
    this.$emit('update-view', viewId)
  }

  /** Emits the event to delete the current search selected */
  deleteSearch (searchId: number) {
    this.$emit('delete-search', searchId)
    this.clearSearchSelected()
    this.setFiltersWith(new FillgridFilters())
  }

  /**
   * Emits the event to create or update a search.
   * If the 'searchId' has a valid value, we will try to update its values by a PUT
   * else we will create a new one by a POST
   *
   * @param filters values of the filters to save the search
   * @param searchId id of the search in case to update its values
   */
  emitSaveSearch (filters: FillgridFilters, searchId?: number) {
    filters.name = this.namefiltersToSave
    if (searchId && searchId > 0) { // PUT
      this.setBackupWith(filters)
      this.$emit('save-search', filters, searchId)
    } else { // POST
      this.$emit('save-search', filters)
    }
    this.$bvModal.hide('save-filters-modal')
  }

  /** Emit a -1 value for the view selected */
  clearSearchSelected () {
    this.emitSearchSelected(UNDEFINED_ENTITY_ID)
  }

  /** Emit a 0 value for the view selected */
  clearViewSelected () {
    this.emitViewSelected(0)
  }

  /**
   * Reset the values in the filters with the backup values
   */
  resetFiltersToBackup () {
    this.setFiltersWith(this.filtersBackup)
  }

  /**
   * Set the 'filters' values as the current 'filters' variable
   *
   * @param filters to set as the current
   */
  setFiltersWith (filters: FillgridFilters) {
    this.filters = Object.assign({}, filters)
    this.filters.origin = SearchOrigin.FILLGRID // all searches created from this component will be of type FILL_GRID
  }

  /**
   * Set the 'filters' values as the current 'filtersBackup' variable
   *
   * @param filters to set as the current backup
   */
  setBackupWith (filters: FillgridFilters) {
    this.filtersBackup = Object.assign({}, filters)
  }

  /**
   * Based on a CustomSearch get its values and set them into the inputs
   *
   * @param search CustomSearch instance to get values
   */
  updateFiltersValues (search: CustomSearch) {
    this.setFiltersWith(new FillgridFilters()) // init the filters to fill with the 'search' values
    this.filters.name = search.name
    this.filters.dateType = search.dateType ?? this.searchDateTypes.RECEIVED
    // When we create an auto custom search for DATAFEED whit 'Todo' as range date
    // the response just has the 'to' date value without 'from' date.
    // Otherwise, the response has a 'from' date defined.
    if (!search.from && search.to) {
      this.filters.dates = []
      this.filters.fullHistory = true
    } else {
      this.filters.dates = [
        search.from
          ? new Date(DateUtils.isUTCFormat(search.from) ? search.from : `${search.from}Z`)
          : new Date(),
        search.to
          ? new Date(DateUtils.isUTCFormat(search.to) ? search.to : `${search.to}Z`)
          : new Date()
      ]
    }

    // manage the status value
    if (search.worked !== undefined && search.worked === true) {
      this.filters.status = 1
    } else if (search.worked !== undefined && search.worked === false) {
      this.filters.status = 2
    } else {
      this.filters.status = 0
    }
    this.filters.user = search.user && search.user.length ? search.user[0] : STRING.EMPTY_STRING
    if (!this.filters.user && search.altUser && search.altUser.length) {
      this.filters.user = search.altUser[0]
    }

    // set the fullSearch
    // due to, we can't know if the filters were filled by multiple inputs or fullSearch input
    // we set them into the -fullSearch- input
    if (search.values) {
      if (this.isUserOrAltUserInFullSearch(search.values)) {
        let userOrAltUser = this.getUserOrAltUserFromFullSearch(search.values)![0]
        this.filters.user = this.getUserOrAltUserFromFilter(userOrAltUser)
        let fullSearch = this.removeSentenceFromFullSearch(userOrAltUser, search.values)
        if (fullSearch.endsWith(' AND ()')) {
          // Remove the residue by user or altUser
          fullSearch = fullSearch.replace(' AND ()', '')
          // Remove de parentesis ()
          if (fullSearch.startsWith('(')) fullSearch = fullSearch.slice(1)
          if (fullSearch.endsWith(')')) fullSearch = fullSearch.slice(0, -1)
        }
        search.values = fullSearch
      }
      this.filters.fullSearch = search.values ?? STRING.EMPTY_STRING
      this.showFullSearchInput(search.values.length > 0)
    }
    // set the idents from the search
    this.filters.idents = []
    this.addIdentsToFilters(search)

    // and process each value ident
    this.filters.idents.forEach(ident => {
      this.addFiltersIds(ident)
    })
    this.filters.view = this.viewId

    // load geoDistance value
    if (search.geoDistance?.kilometers !== 0 && (search.geoDistance?.location.latitude !== 0 || search.geoDistance.location.longitude !== 0)) {
      this.filters.geoDistance = search.geoDistance as GeoDistanceFillgridFilter
    }

    this.setBackupWith(this.filters)
    this.createDateSelectorRangeShortcuts()
  }

  /**
   * Validates if the user or altUser are into the fullSearch
   */
  isUserOrAltUserInFullSearch (fullSearch: string): boolean {
    return FILLGRID_REGEXPS.SEARCH.USER_OR_ALTUSER.test(fullSearch)
  }

  /**
   * Get the pair of values for user and altUser
   * with the form - user:<value> OR altUser:<value> - from the fullSearch
   *
   * @param fullSearch a string with the search values
   * @returns a list with the matches found in the fullSearch
   */
  getUserOrAltUserFromFullSearch (fullSearch: string): RegExpMatchArray | null {
    return fullSearch.match(FILLGRID_REGEXPS.SEARCH.USER_OR_ALTUSER)
  }

  /**
   * Get the last value from the user and altUser, teorically those are the same
   *
   * @param value user:<name> OR altUser:<name> string
   * @returns name value of the user or altUser
   */
  getUserOrAltUserFromFilter (value: string): string {
    let user = STRING.EMPTY_STRING
    let userAndAltUser = value.split(OPERATOR.OR)
    if (userAndAltUser.length) {
      userAndAltUser = userAndAltUser.map((value) => value.trim())
      userAndAltUser.forEach(value => {
        user = value.split(STRING.COLON)[1]
      })
    }
    return user
  }

  /**
   * To remove the sentence setted from the fullSearch string
   *
   * @returns the same fullSearch without the sentence setted
   */
  removeSentenceFromFullSearch (sentence: string | RegExp, fullSearch: string): string {
    return fullSearch.replaceAll(sentence, STRING.EMPTY_STRING)
  }

  addIdentsToFilters (search: CustomSearch) {
    let existIdent0 = CustomSearch.searchHasIdent(search, INDENT_0)
    if (existIdent0 || this.considerIdentForFilters(INDENT_0)) {
      this.filters.idents.push({ insertedIds: existIdent0 ? search.ident0!.toString() : '', ids: [] })
    }
    let existIdent1 = CustomSearch.searchHasIdent(search, INDENT_1)
    if (existIdent1 || this.considerIdentForFilters(INDENT_1)) {
      this.filters.idents.push({ insertedIds: existIdent1 ? search.ident1!.toString() : '', ids: [] })
    }
    let existIdent2 = CustomSearch.searchHasIdent(search, INDENT_2)
    if (existIdent2 || this.considerIdentForFilters(INDENT_2)) {
      this.filters.idents.push({ insertedIds: existIdent2 ? search.ident2!.toString() : '', ids: [] })
    }
  }

  considerIdentForFilters (identIndex: number): boolean {
    return this.identsLabels &&
      identIndex < this.identsLabels.length &&
      identIndex === this.filters.idents.length
  }

  init () {
    /** Idents */
    this.identsNames.forEach(it => {
      this.filters.idents.push(
        {
          insertedIds: '',
          ids: []
        }
      )
    })

    /** Fill autocomplete items for fill text search */
    this.itemsFullSearch = this.columns.reduce((arr: any[], it: any) => {
      return ['signature', 'photo', 'audio', 'pdf', 'location', 'file'].includes(it.type) ? arr : arr.concat(it.base ? it.name.substring(1) + ':' : it.name + ':')
    }, []).concat('AND', 'OR')

    this.showFullSearchInput(false)
  }

  createDateSelectorRangeShortcuts () {
    this.dateRangeShortcuts = [
      {
        text: 'Hoy',
        onClick: () => {
          this.filters.dates = [ new Date(), new Date() ]
          this.filters.fullHistory = false
        }
      },
      {
        text: 'Últimos 7 días',
        onClick: () => {
          this.filters.dates = [ moment().add(-7, 'day').toDate(), new Date() ]
          this.filters.fullHistory = false
        }
      },
      {
        text: 'Últimos 30 días',
        onClick: () => {
          this.filters.dates = [ moment().add(-30, 'day').toDate(), new Date() ]
          this.filters.fullHistory = false
        }
      }
    ]
    if (FillgridService.isFiltersConfigToFTS(this.filters)) {
      this.dateRangeShortcuts.push({
        text: 'Todo',
        onClick: () => {
          this.filters.dates = []
          this.filters.fullHistory = true
        }
      })
    } else {
      this.dateRangeShortcuts.push({
        text: 'Últimos 90 días',
        onClick: () => {
          this.filters.dates = [ moment().add(-90, 'day').toDate(), new Date() ]
          this.filters.fullHistory = false
        }
      })
    }
  }

  bottomLimitDate (date : Date, days : number) {
    date.setDate(date.getDate() - days)
    return date
  }
  topLimitDate (date : Date, days : number) {
    date.setDate(date.getDate() + days)
    return date
  }

  unavailableDatesInRange (date: Date, currentValue: Date[]) {
    return date >= new Date() || date <= this.bottomLimitDate(new Date(currentValue[0]), 90) || date >= this.topLimitDate(new Date(currentValue[0]), 90)
  }

  getPlaceholderDates () {
    return FillgridService.isFiltersConfigToFTS(this.filters) ? 'Todo' : '--Seleciona un rango de fechas--'
  }

  onPasteIds (event: any, ident: any) {
    event.stopPropagation()
    event.preventDefault()
    const clipboardData = event.clipboardData || (window as any).clipboardData
    const pastedData = clipboardData.getData('Text')
    ident.insertedIds = pastedData.replace(/,+|\n+|\s+/g, ',')
  }

  addFiltersIds (ident: any) {
    if (!ident.insertedIds.length) return
    let ids = ident.insertedIds.split(/,|\n+/)
    ids = ids.filter((it: any) => it.length)
    ident.ids = ident.ids.concat(ids)
    ident.insertedIds = ''
  }

  removeFiltersId (index: number, ident: any) {
    ident.ids.splice(index, 1)
  }

  clearFiltersIds (ident: any) {
    ident.ids = []
  }

  /** CUSTOM FILTERS */

  addCriteria (field: string) {
    const columnConfig = this.columns.find(it => it.name === field) || {} as any
    this.filters.custom.push({
      field: field,
      union: this.filters.custom.length ? ' AND ' : '',
      end: '',
      operator: 'equals',
      name: columnConfig.name,
      label: columnConfig.label,
      type: columnConfig.type,
      options: columnConfig.type === TemplateQuestionsTypes.SELECT ? Object.entries((columnConfig.questionRef.display && columnConfig.questionRef.display.options) || {}).map(it => { return { name: it[0], value: it[1] } }) : [],
      displayType: (columnConfig.questionRef.display && columnConfig.questionRef.display.type) || ''
    })
    this.customFilterSelected = ''
    setTimeout(() => {
      const refName = 'input' + (this.filters.custom.length - 1)
      const refs = this.$refs[refName] as Element[]
      if (refs && refs.length) (refs[0] as any).focus()
    }, 500)
  }

  removeCustomFilter (index: number) {
    if (index === 0 && this.filters.custom.length > 1) {
      let itemNext = this.filters.custom[index + 1] as any
      itemNext.union = itemNext.union.indexOf('(') > -1 ? '( ' : ''
    }

    if (index > 0 && index === this.filters.custom.length - 1) {
      let itemPrevious = this.filters.custom[index - 1] as any
      itemPrevious.end = itemPrevious.end.indexOf(')') > -1 ? ' )' : ''
    }

    this.filters.custom.splice(index, 1)
  }

  moveBackward (index: number) {
    this.filters.custom.splice(index - 1, 0, this.filters.custom.splice(index, 1)[0])
  }

  moveForward (index: number) {
    this.filters.custom.splice(index + 1, 0, this.filters.custom.splice(index, 1)[0])
  }

  focusDateSelector () {
    const dateSelector = document.getElementById('dateSelectorFilter')
    const directSonDate = dateSelector?.children[0]
    const childrenDate = directSonDate?.children
    const inputDate = childrenDate?.namedItem('date') as HTMLElement
    inputDate?.focus()
  }

  /** FULL SEARCH INPUT */

  /**
   * @param show true to show the fullsearch input
   */
  showFullSearchInput (show: boolean) {
    this.fullSearchVisible = show
    this.createDateSelectorRangeShortcuts()
  }

  openFullSearchInput () {
    this.showFullSearchInput(true)
    setTimeout(() => {
      const el = document.getElementsByClassName('fillgrid-full-search-input') as any
      if (el && el.length) el[0].focus()
    }, 200)
  }

  /** **** ACTIONS ******/

  search () {
    if (this.filters.dates[0] == null && !this.validateFTSFilters()) {
      this.$bvToast.toast('No se ha definido un rango de fechas para consultar.', {
        title: 'Error',
        variant: 'danger',
        toaster: 'b-toaster-top-left',
        autoHideDelay: 5000
      })
      this.focusDateSelector()
    } else {
      if (this.isNewSearch()) {
        this.lastRequestTimestamp = new Date()
        this.$emit('search', this.filters)
        if (this.searchIdSelected > 0) {
          this.emitSearchSelected(this.searchIdSelected)
        }
        this.saveCustomSearchInLocalStorage() // save the filters in the localStorage
      } else {
        const millisecondsToWait = this.millisecondsToWaitForNextRequest - (new Date().getTime() - this.lastRequestTimestamp.getTime())
        const secondsToWait = Math.ceil(millisecondsToWait / 1000)
        this.$emit('simulate-search', secondsToWait)
      }
    }
  }

  isNewSearch (): boolean {
    const fillName = cookiesService.get(BM_COOKIES.UPDATE_FILL_IN_FILLGRID)
    if (JSON.stringify(this.lastRequestFilters) !== JSON.stringify(this.filters)) {
      return true
    }
    if (this.isUpdating && fillName) {
      return false
    }
    const currentTime = new Date()
    if (currentTime.getTime() - this.lastRequestTimestamp.getTime() > this.millisecondsToWaitForNextRequest) {
      return true
    }
    return false
  }
  reloadUsers () {
    let pageRequest = new PageRequest<UserCatalogFilters>()
    if (userInfoStore.userInfo.domain.name === 'root') {
      pageRequest.filters.domainId = cookiesService.get(config.domainIdCookieName)
    }
    this.loadingUsers = true
    localStorageUser.reloadListUser(pageRequest).then(list => {
      setTimeout(() => {
        this.$emit('update-users', list)
        this.loadingUsers = false
        this.$bvToast.toast('La lista de usuarios ha sido refrescada correctamente.', {
          title: 'Lista de usuario refrescada',
          variant: 'success',
          toaster: 'b-toaster-top-left',
          autoHideDelay: 5000
        })
      }, 500)
    })
  }
  saveCustomSearchInLocalStorage () {
    this.lastRequestFilters = JSON.parse(JSON.stringify(this.filters))
    const customSearch = FillgridService.fillgridFiltersToCustomSearch(this.filters)
    FillgridService.saveCustomSearchInLocalStorage(this.templateId, customSearch)
  }

  openSaveFiltersModal () {
    this.namefiltersToSave = ''
    if (this.searchIdSelected > 0) {
      this.namefiltersToSave = this.filters.name
    }
    this.$bvModal.show('save-filters-modal')
  }

  clearFilters () {
    this.filters = new FillgridFilters()
    this.clearSearchSelected()
    this.clearViewSelected()
    this.init()
  }

  loadSavedFilters (filterId: number) {
    if (filterId === 0) {
      this.filters = JSON.parse(JSON.stringify(this.filtersBackup))
    } else {
      const filter = this.savedFiltersList.find(it => it.id === filterId)
      this.savedFilterNameSelected = filter.name
      this.filters = JSON.parse(JSON.stringify(filter.filters))
    }
    this.namefiltersToSave = ''
    this.createDateSelectorRangeShortcuts()
    this.search()
  }

  openDeleteFilters () {
    this.$bvModal.msgBoxConfirm('¿Deseas elimnar permanentemente "' + this.filters.name + '" de la lista?', {
      title: 'Confirmación',
      size: 'sm',
      okVariant: 'danger',
      okTitle: 'Sí',
      cancelTitle: 'No',
      footerClass: 'p-2',
      hideHeaderClose: false,
      centered: true
    })
      .then(confirmed => {
        if (confirmed) {
          this.deleteSearch(this.searchIdSelected)
        }
      })
  }
  validateFTSFilters () {
    if (!FillgridService.isFiltersConfigToFTS(this.filters)) {
      this.filters.dates = [ new Date(), new Date() ]
      this.filters.fullHistory = false
      return false
    }
    return true
  }
  getTextSearch () {
    return this.isUpdating ? 'Refrescar/Buscar' : 'Buscar'
  }
}
